import React from 'react'
import styles from './DonutChart.module.scss'
import { PieChart, Pie, Cell, ResponsiveContainer, Label } from 'recharts'
import cx from 'classnames'

// recharts types ResponsiveContainer as taking a single child, but at runtime
// it maps over whatever it is given; the array case is explicitly handled (it
// just skips the chart style injection). This chart stacks two PieCharts.
type MultiChartContainerProps = Omit<
  React.ComponentProps<typeof ResponsiveContainer>,
  'children'
> & { children?: React.ReactNode }

const MultiChartContainer =
  ResponsiveContainer as unknown as React.FC<MultiChartContainerProps>

const themes = [
  {
    graph: ['#C2DBE7', '#0D9FCC'],
    label: '#016787',
    pill: '#2187A7',
  },
  {
    graph: ['#B9E4DA', '#4B9483'],
    label: '#3B8473',
    pill: '#4B9483',
  },
]

type DonutTheme = { graph: string[]; label: string; pill: string }

type CustomLabelProps = {
  viewBox?: { cx?: number; cy?: number }
  value?: number
  scoreLabel?: React.ReactNode
  theme: DonutTheme
}

const CustomLabel = ({
  viewBox,
  value,
  scoreLabel,
  theme,
}: CustomLabelProps) => {
  return (
    <g>
      <text
        x={viewBox?.cx}
        y={viewBox?.cy}
        textAnchor="middle"
        dominantBaseline="central"
        alignmentBaseline="middle"
        className={cx('recharts-text recharts-label', {
          [styles.label]: true,
        })}
        style={{ fill: theme.label }}
      >
        {scoreLabel}
      </text>
    </g>
  )
}

type DonutChartProps = {
  value: number
  scoreLabel?: React.ReactNode
  isGreen?: boolean
}

export const DonutChart = ({
  value,
  scoreLabel,
  isGreen = false,
}: DonutChartProps) => {
  const data01 = [
    { name: 'value', value: value },
    { name: 'empty space', value: 100 - value },
  ]

  const theme = themes[Number(isGreen)]

  return (
    <div className={styles.donutChart}>
      <MultiChartContainer className={styles.responsiveContainer}>
        <PieChart className={styles.background}>
          <Pie
            data={[{ name: 'value', value: 100 }]}
            dataKey="value"
            cx={78}
            cy={78}
            innerRadius={58}
            outerRadius={78}
            cornerRadius={10}
            isAnimationActive={false}
          >
            <Cell fill={'#f5f5f5'} />
          </Pie>
        </PieChart>
        <PieChart>
          <defs>
            <linearGradient id={`myGradient${isGreen}`}>
              <stop offset="0%" stopColor={theme.graph[0]} />
              <stop offset="100%" stopColor={theme.graph[1]} />
            </linearGradient>
          </defs>
          <Pie
            data={data01}
            dataKey="value"
            cx={78}
            cy={78}
            innerRadius={58}
            outerRadius={78}
            cornerRadius={10}
          >
            {data01.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={index ? 'transparent' : `url(#myGradient${isGreen})`}
                className={index ? styles.hiddenCell : undefined}
              />
            ))}
            <Label
              content={
                <CustomLabel
                  scoreLabel={scoreLabel}
                  value={value}
                  theme={theme}
                />
              }
              position="center"
            />
          </Pie>
        </PieChart>
      </MultiChartContainer>
    </div>
  )
}
